import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { useStore } from '../stores/store';
import { exportSeedPhrase, restoreIdentity, getIdentity } from '../lib/identity';
import { isOffensive } from '../lib/badwords';
import { teamName } from '../lib/flags';
import { TIMEZONES } from '../lib/timezones';
import { H2, BackLink, Muted } from '../components/ui';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  label { display: block; font-weight: 700; margin-bottom: 8px; }
  input[type='text'], select {
    width: 100%;
    box-sizing: border-box;
    font-size: 1rem;
    padding: 11px;
    border-radius: 10px;
    border: 1.5px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Seg = styled.div`
  display: flex;
  gap: 8px;
  button {
    flex: 1;
    padding: 11px;
    border-radius: 10px;
    font-weight: 700;
    border: 1.5px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
  }
  button.on {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  button {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.colors.border};
    cursor: pointer;
  }
  button.on { border-color: ${({ theme }) => theme.colors.text}; box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.text}; }
  input[type='color'] { width: 40px; height: 34px; border: none; background: none; padding: 0; }
`;

const About = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  font-size: 0.9rem;
  line-height: 1.4;
  li b { color: ${({ theme }) => theme.colors.text}; }
`;

const OracleBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #1a1340, ${({ theme }) => theme.colors.accent});
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 13px;
  font-weight: 800;
  font-size: 0.98rem;
`;

const PRESETS = [
  { name: 'Verde (por defecto)', hex: '' },
  { name: 'Azul', hex: '#2563eb' },
  { name: 'Violeta', hex: '#7c3aed' },
  { name: 'Rojo', hex: '#e11d48' },
  { name: 'Naranja', hex: '#ea580c' },
  { name: 'Turquesa', hex: '#0891b2' },
];
const DEFAULT_GREEN = '#1aa86a';

export function Preferencias() {
  const navigate = useNavigate();
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const nick = useStore((s) => s.nick);
  const setNick = useStore((s) => s.setNick);
  const favorite = useStore((s) => s.favorite);
  const tz = useStore((s) => s.tz);
  const setTz = useStore((s) => s.setTz);
  const accent = useStore((s) => s.accent);
  const setAccent = useStore((s) => s.setAccent);
  const setOracle = useStore((s) => s.setOracleTuning);

  const [nickInput, setNickInput] = useState(nick);
  const teams = useAsync(() => api.teams(), [], '/teams');

  const [showKey, setShowKey] = useState(false);
  const [importVal, setImportVal] = useState('');
  const [keyMsg, setKeyMsg] = useState('');

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(exportSeedPhrase());
      setKeyMsg('Frase de 24 palabras copiada al portapapeles');
    } catch {
      setShowKey(true); // sin portapapeles: al menos mostrarla para copiar a mano
    }
  };

  // Graba el nombre en el server (no solo local) para que el cambio sea persistente y se propague a
  // los otros dispositivos de la cuenta. El server solo actualiza si ya sos jugador de la Timba.
  const saveNick = async (raw: string) => {
    const clean = raw.trim();
    if (!clean || clean === nick || isOffensive(clean)) return;
    setNick(clean); // optimista: el cambio se ve al instante
    try {
      const { pub } = getIdentity();
      const r = await api.timbaSetNick(pub, clean);
      if (r.nick && r.nick !== clean) setNick(r.nick); // adoptar el final del server (dedup/filtro)
    } catch {
      /* sin red: queda local y se grabará en la próxima acción de la Timba */
    }
  };

  const doImport = async () => {
    try {
      const id = restoreIdentity(importVal);
      // Traer también el nombre del jugador restaurado (si no, queda el nick de la sesión anterior,
      // y peor: en el próximo guardado le pisaría el nombre real al usuario recuperado).
      const d = await api.timbaPlayer(id.pub).catch(() => null);
      if (d?.nick) setNick(d.nick);
      setKeyMsg('Identidad restaurada. Recargando…');
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      setKeyMsg(e instanceof Error ? e.message : 'No se pudo restaurar');
    }
  };

  return (
    <>
      <BackLink onClick={() => navigate(-1)}>← Volver</BackLink>
      <h2 style={{ marginTop: 0 }}>Preferencias</h2>

      <H2>Tema</H2>
      <Card>
        <Seg>
          <button className={theme === 'dark' ? 'on' : ''} onClick={() => theme !== 'dark' && toggleTheme()}>
            Oscuro
          </button>
          <button className={theme === 'light' ? 'on' : ''} onClick={() => theme !== 'light' && toggleTheme()}>
            Claro
          </button>
        </Seg>
      </Card>

      <H2>Tu nombre</H2>
      <Card>
        <Field>
          <input
            type="text"
            maxLength={24}
            value={nickInput}
            placeholder="Tu nombre o apodo"
            onChange={(e) => setNickInput(e.target.value)}
            onBlur={() => saveNick(nickInput)}
          />
          {nickInput.trim().length >= 2 && isOffensive(nickInput) && (
            <p style={{ color: '#ff6b6b', fontSize: '0.8rem', margin: '6px 0 0' }}>Ese apodo no está permitido. Elegí otro.</p>
          )}
        </Field>
      </Card>

      <H2>Equipo favorito</H2>
      <Card>
        <Field>
          <Async state={teams}>
            {(d) => (
              <select
                value={favorite ?? ''}
                onChange={(e) => useStore.setState({ favorite: e.target.value || null })}
              >
                <option value="">Ninguno</option>
                {d.teams.map((t) => (
                  <option key={t.name} value={t.name}>
                    {teamName(t.name)}
                  </option>
                ))}
              </select>
            )}
          </Async>
        </Field>
      </Card>

      <H2>Zona horaria</H2>
      <Card>
        <Field>
          <select value={tz} onChange={(e) => setTz(e.target.value)}>
            {TIMEZONES.map((o) => (
              <option key={o.tz || 'device'} value={o.tz}>
                {o.label}
              </option>
            ))}
          </select>
          <Muted style={{ marginTop: 8 }}>Las horas de los partidos se muestran en esta zona.</Muted>
        </Field>
      </Card>

      <H2>Color principal</H2>
      <Card>
        <Swatches>
          {PRESETS.map((p) => (
            <button
              key={p.hex || 'def'}
              title={p.name}
              className={accent === p.hex ? 'on' : ''}
              style={{ background: p.hex || DEFAULT_GREEN }}
              onClick={() => setAccent(p.hex)}
            />
          ))}
          <input
            type="color"
            value={accent || DEFAULT_GREEN}
            onChange={(e) => setAccent(e.target.value)}
            title="Color personalizado"
          />
        </Swatches>
        <Muted style={{ marginTop: 10 }}>Tocá un color o elegí uno propio. El verde es el de fábrica.</Muted>
      </Card>

      <H2>El Oráculo</H2>
      <Card>
        <Muted style={{ marginBottom: 10 }}>
          Ajustá cuánto pesa cada fuente en las predicciones de los partidos.
        </Muted>
        <OracleBtn onClick={() => setOracle(true)}>🔮 Ajuste del Oráculo</OracleBtn>
      </Card>

      <H2>Identidad y respaldo (Timba)</H2>
      <Card>
        <Muted style={{ marginBottom: 10 }}>
          Tu identidad vive solo en este dispositivo. Guardá tu <strong>frase de 24 palabras</strong> para no
          perder tus pronósticos y torneos si cambiás de teléfono, o para jugar desde otro dispositivo.
        </Muted>
        <Seg style={{ marginBottom: 10 }}>
          <button onClick={copyKey}>Copiar mis 24 palabras</button>
          <button onClick={() => setShowKey((v) => !v)}>{showKey ? 'Ocultar' : 'Ver mis palabras'}</button>
        </Seg>
        {showKey && (
          <Field>
            <textarea readOnly rows={3} value={exportSeedPhrase()} onFocus={(e) => e.target.select()} style={{ width: '100%', boxSizing: 'border-box', resize: 'none', font: 'inherit' }} />
            <Muted style={{ marginTop: 8 }}>⚠️ Es secreta: cualquiera con estas 24 palabras es tu identidad. No las compartas.</Muted>
          </Field>
        )}
        <Field style={{ marginTop: 12 }}>
          <label>Restaurar desde la frase (o clave)</label>
          <input
            type="text"
            value={importVal}
            placeholder="Pegá tus 24 palabras (o la clave)"
            onChange={(e) => setImportVal(e.target.value)}
          />
        </Field>
        <Seg style={{ marginTop: 10 }}>
          <button className="on" onClick={doImport} disabled={!importVal.trim()}>
            Restaurar identidad
          </button>
        </Seg>
        {keyMsg && <Muted style={{ marginTop: 10, fontWeight: 700 }}>{keyMsg}</Muted>}
      </Card>

      <H2>Acerca de</H2>
      <Card>
        <About>
          <li><b>🔮 El Oráculo</b> — pronóstico de cada partido: favorito, probabilidad 1-X-2 y los factores que pesan. Entrenalo a tu gusto.</li>
          <li><b>🟢 En vivo</b> — marcador y minuto al instante, con goleadores y pizarra a pantalla completa.</li>
          <li><b>📊 Ficha del partido</b> — estadísticas, alineaciones, eventos, árbitro y clima de la sede.</li>
          <li><b>📅 Partidos y Hoy</b> — calendario completo, lo que se juega ahora y los últimos resultados.</li>
          <li><b>🧮 Zonas</b> — tablas de los 12 grupos, calculadas desde los resultados.</li>
          <li><b>🎲 Timba</b> — prode de la fase de llave: pronosticá cada partido, sumá puntos y armá torneos privados con tus amigos.</li>
          <li><b>📚 Histórico</b> — todos los Mundiales 1930-2022 con sus datos.</li>
          <li><b>⭐ A tu medida</b> — equipo favorito, tema claro/oscuro, color, zona horaria.</li>
        </About>
        <Muted style={{ marginTop: 10, textAlign: 'center' }}>VamoVamo! · hecho para el hincha 💚</Muted>
      </Card>
    </>
  );
}
